import { useCallback, useEffect, useMemo, useState } from "react";
import type { HotelRoomRateMeta } from "@b2bportal/core-types";
import { uniqBy } from "lodash-es";

export interface UseBaseRateSelectionProps {
  rates: HotelRoomRateMeta[];
  selectedRate: HotelRoomRateMeta;
  setSelectedRate: (rate: HotelRoomRateMeta) => void;
}

export interface UseBaseRateSelectionReturn {
  baseRates: HotelRoomRateMeta[];
  selectedBaseRate: HotelRoomRateMeta;
  onSelectBaseRateId: (baseRateId: string) => void;
}

/**
 * Hook that manages base rate selection (unique by cancellation policy + origin).
 * Tracks the currently selected base rate and provides a handler to change it.
 */
export const useBaseRateSelection = ({
  rates,
  selectedRate,
  setSelectedRate,
}: UseBaseRateSelectionProps): UseBaseRateSelectionReturn => {
  const baseRates = useMemo(() => {
    return uniqBy(
      rates,
      (rate) =>
        `${rate.product.cancellationPolicyV2.CancellationPolicyV2}//${rate.origin}`
    );
  }, [rates]);

  const [selectedBaseRate, setSelectedBaseRate] =
    useState<HotelRoomRateMeta | null>(null);

  // Initialize selectedBaseRate to match the initial selectedRate's cancellation policy
  useEffect(() => {
    if (selectedBaseRate == null && baseRates.length > 0) {
      const selectedCancellation =
        selectedRate.product.cancellationPolicyV2.CancellationPolicyV2;
      const selectedOrigin = selectedRate.origin;

      const matchingBaseRate = baseRates.find(
        (r) =>
          r.product.cancellationPolicyV2.CancellationPolicyV2 ===
            selectedCancellation && r.origin === selectedOrigin
      );

      setSelectedBaseRate(matchingBaseRate ?? baseRates[0]);
    }
  }, [baseRates, selectedRate, selectedBaseRate]);

  const onSelectBaseRateId = useCallback(
    (baseRateId: string) => {
      const baseRate = baseRates.find((r) => r.key === baseRateId);
      if (baseRate) {
        setSelectedBaseRate(baseRate);
        setSelectedRate(baseRate);
      }
    },
    [baseRates, setSelectedRate]
  );

  // Use selectedBaseRate if available, otherwise fall back to first base rate
  const effectiveSelectedBaseRate = selectedBaseRate ?? baseRates[0];

  return {
    baseRates,
    selectedBaseRate: effectiveSelectedBaseRate,
    onSelectBaseRateId,
  };
};
